import type { StopLabelPlacementConfig, StopLabelVisualVariant } from "./stopLabel.types";

/** Shell corner radius (px) shared by itinerary and realtime stop labels (`rounded-md`). */
export const STOP_LABEL_SHELL_BORDER_RADIUS_PX = 6;

/** Border radius for all transit-path stop label shells. */
export const STOP_LABEL_SHELL_BORDER_RADIUS_CLASS = "rounded-md";

/** @deprecated Use {@link STOP_LABEL_SHELL_BORDER_RADIUS_CLASS}. */
export const STOP_LABEL_BUBBLE_BORDER_RADIUS_CLASS = STOP_LABEL_SHELL_BORDER_RADIUS_CLASS;

/** @deprecated Use {@link STOP_LABEL_SHELL_BORDER_RADIUS_CLASS}. */
export const STOP_LABEL_ENDPOINT_BORDER_RADIUS_CLASS = STOP_LABEL_SHELL_BORDER_RADIUS_CLASS;

/**
 * Resolves the shell border-radius class for a stop label visual variant.
 *
 * @param _variant Stop label visual preset (all variants share the same shell radius).
 * @returns Tailwind border-radius utility matching the label shell.
 */
export function resolveStopLabelBorderRadiusClass( variant: StopLabelVisualVariant ): string {
    void variant;

    return STOP_LABEL_SHELL_BORDER_RADIUS_CLASS;
}

/**
 * Resolves a slightly tighter border-radius for the arrival-time pill.
 *
 * @param _variant Stop label visual preset.
 * @returns Tailwind border-radius utility one step smaller than the shell.
 */
export function resolveStopLabelArrivalTimePillBorderRadiusClass( variant: StopLabelVisualVariant ): string {
    void variant;

    return "rounded-sm";
}

/** Fixed outer width (px) for all transit-path stop label shells. */
export const STOP_LABEL_SHELL_WIDTH_PX = 150;

/** Fixed width and overflow rules shared by all transit-path stop label shells. */
export const STOP_LABEL_SHELL_LAYOUT_CLASS =
    "box-border w-[155px] overflow-hidden text-left";

/** Horizontal inner padding (px) for stop label shells. */
export const STOP_LABEL_SHELL_PADDING_X_PX = 4;

/** Vertical inner padding (px) for stop label shells. */
export const STOP_LABEL_SHELL_PADDING_Y_PX = 4;

/** Shared inner padding for itinerary and realtime stop label shells (mirrors inline shell padding). */
export const STOP_LABEL_SHELL_PADDING_CLASS = "px-1.5 py-1";

/** Shared stop-name typography for bubble and endpoint chip shells. */
export const STOP_LABEL_SHELL_TEXT_CLASS = "text-[10px] font-semibold leading-snug";

/** Truncated stop name cell aligned to the left edge of the available space. */
export const STOP_LABEL_NAME_CLASS =
    `min-w-0 pr-1 flex-1 truncate text-left ${ STOP_LABEL_SHELL_TEXT_CLASS }`;

/** Full-width left-aligned stop name when no arrival-time pill is shown. */
export const STOP_LABEL_NAME_ONLY_CLASS =
    `min-w-0 w-full truncate text-left ${ STOP_LABEL_SHELL_TEXT_CLASS }`;

/** Horizontal row layout: name on the left, arrival-time pill on the right. */
export const STOP_LABEL_CONTENT_ROW_CLASS =
    "flex w-full items-center justify-between leading-none";

/** Fixed width (px) per correspondence badge (four per row inside the 155px shell). */
export const STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_PX = 33;

/**
 * Tailwind helpers for correspondence badge fixed width (mirrors {@link STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_PX}).
 */
export const STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_CLASS =
    "w-[33px] min-w-[33px] max-w-[33px] shrink-0 flex-none";

/**
 * Applies the fixed correspondence badge width via inline styles (immune to Tailwind purge).
 *
 * @param element Route or ellipsis badge element.
 */
export function applyStopLabelCorrespondenceBadgeFixedWidth( element: HTMLElement ): void {
    const widthPx = `${ STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_PX }px`;
    element.style.width = widthPx;
    element.style.minWidth = widthPx;
    element.style.maxWidth = widthPx;
    element.style.flexShrink = "0";
}

/**
 * Applies fixed shell width and inner padding via inline styles (immune to Tailwind purge).
 *
 * @param element Outer stop label shell element.
 */
export function applyStopLabelShellLayout( element: HTMLElement ): void {
    const widthPx = `${ STOP_LABEL_SHELL_WIDTH_PX }px`;
    element.style.boxSizing = "border-box";
    element.style.width = widthPx;
    element.style.minWidth = widthPx;
    element.style.maxWidth = widthPx;
    element.style.paddingLeft = `${ STOP_LABEL_SHELL_PADDING_X_PX }px`;
    element.style.paddingRight = `${ STOP_LABEL_SHELL_PADDING_X_PX }px`;
    element.style.paddingTop = `${ STOP_LABEL_SHELL_PADDING_Y_PX }px`;
    element.style.paddingBottom = `${ STOP_LABEL_SHELL_PADDING_Y_PX }px`;
    element.style.borderRadius = `${ STOP_LABEL_SHELL_BORDER_RADIUS_PX }px`;
}

/** MapLibre anchor/offset for intermediate stop labels (always above the dot). */
export const STOP_LABEL_ABOVE_DOT_CONFIG: StopLabelPlacementConfig = {
    "anchor":    "bottom",
    "offset":    [ 0, -12 ],
    "className": "text-left",
};
